// src/components/Cabinet/SearchHeader.jsx
import React, { useState, useEffect, useRef, useContext } from 'react';
import { useTranslation } from 'react-i18next'; 
import { SearchContext } from '../../context/SearchContext';
import "./SearchHeader.css";
import LanguageSwitcher from './LanguageSwitcher';

/**
 * SearchHeader
 * - title: заголовок текущей вкладки (string)
 * - debounceMs: задержка перед обновлением контекста (по умолчанию 250ms)
 */
const SearchHeader = ({ title = '', debounceMs = 250, placeholder }) => {
  const { t } = useTranslation("cabinetpage"); 
  const { query, setQuery } = useContext(SearchContext);
  const [local, setLocal] = useState(query || '');
  const timerRef = useRef(null);
  const inputRef = useRef(null);
  const [isMenuVisible, setIsMenuVisible] = useState(false);

  // Если пропс placeholder не передан, берем локализованный дефолтный вариант
  const currentPlaceholder = placeholder || t("searchHeader.default_placeholder");

  // Синхронизируем локальный инпут с внешним query
  useEffect(() => {
    setLocal(query || '');
  }, [query]);

  // Дебаунс: обновляем глобальный query через debounceMs
  useEffect(() => {
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      setQuery(prev => {
        if (prev === (local || '')) return prev;
        return local || '';
      });
    }, debounceMs);

    return () => clearTimeout(timerRef.current);
  }, [local, debounceMs, setQuery]);

  // Быстрая отправка по Enter
  const onKeyDown = (e) => {
    if (e.key === 'Enter') {
      clearTimeout(timerRef.current);
      setQuery(local || '');
      inputRef.current?.blur();
    }
    if (e.key === 'Escape') {
      clearTimeout(timerRef.current);
      setLocal('');
      setQuery('');
      inputRef.current?.blur();
    }
  };

  // BURGER: open/close via dispatching global events
  const toggleMenu = () => {
    window.dispatchEvent(new CustomEvent('menu:toggle'));
  };

  // track menu state for updating icon
  useEffect(() => {
    const onOpen = () => setIsMenuVisible(true);
    const onClose = () => setIsMenuVisible(false);
    window.addEventListener('menu:open', onOpen);
    window.addEventListener('menu:close', onClose);
    window.addEventListener('menu:toggle', () => setIsMenuVisible(s => !s));
    return () => {
      window.removeEventListener('menu:open', onOpen);
      window.removeEventListener('menu:close', onClose);
      window.removeEventListener('menu:toggle', () => setIsMenuVisible(s => !s));
    };
  }, []);

  return (
    <header className="header">
      <div className="header-actions">
        {/* burger button only visible on small screens */}
        <button
          className={`burger-btn ${isMenuVisible ? 'open' : ''}`}
          aria-label={isMenuVisible ? t("searchHeader.aria_menu_close") : t("searchHeader.aria_menu_open")}
          aria-expanded={isMenuVisible}
          onClick={toggleMenu}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" aria-hidden="true">
            <g stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <line className="b1" x1="3" y1="6" x2="20" y2="6" />
              <line className="b2" x1="3" y1="12" x2="16" y2="12" />
              <line className="b3" x1="3" y1="18" x2="12" y2="18" />
            </g>
          </svg>
        </button>
        <div className="cabinet-title-wrap">
          <h2 className="cabinet-title" title={title}>{title}</h2>
        </div>
      </div>
      
      <LanguageSwitcher/>
      <div className="cabinet-search" role="search" aria-label={t("searchHeader.aria_search_role")}>
        <input
          ref={inputRef}
          type="search"
          value={local}
          placeholder={currentPlaceholder}
          onChange={(e) => setLocal(e.target.value)}
          onKeyDown={onKeyDown}
          aria-label={t("searchHeader.aria_search_input")}
        />
      </div>
    </header>
  );
};

export default SearchHeader;